import React, { useEffect, useState } from 'react';
import I18N from '@leverege/i18n';
import { useValueChange } from '@leverege/ui-hooks';
import { Text } from '@leverege/ui-elements';
import { LocationAlertsModel as ModelFactory } from '@leverege/recovr-models';
import { LoadingView } from '@leverege/molten/views';
import { GlobalState } from '@leverege/ui-redux';
import AlertEditor from './AlertEditor';
import { getModelFromFactory } from '../../util/Model';
import S from './LocationAlertsEditor.css';
import MultiSelector from '../../views/salesFlow/components/MultiSelector';
import SendPendingSaleMessageScreen from '../views/SendPendingSaleMessageScreen';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t
} = I18N.ns('recovr.alerts.editor.LocationAlertsEditor');
export default function LocationAlertsEditor(props) {
  const {
    value,
    actions,
    recipients
  } = props;
  const [showDialog, setShowDialog] = useState(false);
  const [combinedRecipients, setCombinedRecipients] = useState(recipients ? Object.values(recipients).reduce((arr, curr) => arr.concat(curr), []).flat() : []);
  useEffect(() => {}, [combinedRecipients]);
  const Model = getModelFromFactory(ModelFactory);
  const onModelChange = useValueChange(Model, props);
  const onAdd = () => {
    setShowDialog(true);
  };
  const onClose = () => {
    setShowDialog(false);
  };
  const onRemoveRecipient = async ({
    data
  }) => {
    // filter out deleted recipient 
    // collections has variable array structures, so flatten them to filter out the deleted recipient
    let type, newValues;
    const closeLoading = LoadingView.showDialog(t('loading'));
    if (typeof data === 'object') {
      type = 'users';
      const recipientUsers = recipients.users.flat(Infinity);
      newValues = recipientUsers.filter(user => user.id !== data.id);
      setCombinedRecipients(prevRecipients => prevRecipients.filter(user => user.id !== data.id));
    } else if (data.includes('@')) {
      type = 'emails';
      const recipientEmails = recipients.emails.flat(Infinity);
      newValues = recipientEmails.filter(email => email !== data);
      setCombinedRecipients(prevRecipients => prevRecipients.filter(email => email !== data));
    } else if (typeof Number(data.replace(/\D/g, '')) === 'number') {
      type = 'phones';
      const recipientPhones = recipients.phones.flat(Infinity);
      newValues = recipientPhones.filter(phone => phone !== data);
      setCombinedRecipients(prevRecipients => prevRecipients.filter(phone => phone !== data));
    }
    // update recipients list
    try {
      await GlobalState.dispatch(actions.update({
        data: [{
          path: `pendingSaleAlerts/${type}`,
          value: newValues
        }]
      }));
    } catch (err) {
      console.error('Could not update location with new pending sale alert recipients', err);
    }
    closeLoading();
  };
  return /*#__PURE__*/_jsxs("div", {
    className: S.content,
    children: [/*#__PURE__*/_jsx(Text, {
      variant: "alertTitleFull|title",
      children: t('closeOfBusinessTitle')
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertDetailLabel|detailLabel",
      children: t('closeOfBusinessMsg')
    }), /*#__PURE__*/_jsx(AlertEditor, {
      kind: "report",
      value: Model.getCloseOfBusinessAlert(value),
      editableValues: Model.editableValuesByAlertType.closeOfBusiness,
      eventData: "setCloseOfBusinessAlert",
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertTitleFull|title",
      children: t('suspectedDriveOffTitle')
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertDetailLabel|detailLabel",
      children: t('suspectedDriveOffMsg')
    }), /*#__PURE__*/_jsx(AlertEditor, {
      kind: "alert",
      value: Model.getSuspectedDriveOffAlert(value),
      editableValues: Model.editableValuesByAlertType.suspectedDriveOff,
      eventData: "setSuspectedDriveOffAlert",
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertTitleFull|title",
      children: t('pendingSaleTitle')
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertDetailLabel|detailLabel",
      children: t('pendingSaleMsg')
    }), /*#__PURE__*/_jsx(AlertEditor, {
      kind: "alert",
      value: Model.getPendingSaleAlert(value),
      editableValues: Model.editableValuesByAlertType.pendingSale,
      eventData: "setPendingSaleAlert",
      onChange: onModelChange
    }), /*#__PURE__*/_jsxs(Text, {
      variant: "bodySmall",
      className: S.usersCount,
      children: [combinedRecipients?.length, " recipients"]
    }), /*#__PURE__*/_jsx(MultiSelector, {
      values: combinedRecipients,
      valueKey: "id",
      onAdd: onAdd,
      onRemove: onRemoveRecipient
    }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx("hr", {
      className: S.space
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertTitle|title",
      children: "Theft Prevention Alerts"
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertDetailTitle|detailTitle",
      children: t('theftSubtitle')
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertDetailLabel|detailLabel",
      children: t('theftMsg')
    }), /*#__PURE__*/_jsx(AlertEditor, {
      kind: "alert",
      value: Model.getLeftLocationAfterHoursAlert(value),
      editableValues: Model.editableValuesByAlertType.leftLocationAfterHours,
      eventData: "setLeftLocationAfterHoursAlert",
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertDetailTitle|detailTitle",
      children: t('vehicleFarTitle')
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertDetailLabel|detailLabel",
      children: t('vehicleFarMsg')
    }), /*#__PURE__*/_jsx(AlertEditor, {
      kind: "alert",
      value: Model.getFarFromSiteAlert(value),
      editableValues: Model.editableValuesByAlertType.farFromSite,
      eventData: "setFarFromSiteAlert",
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertDetailTitle|detailTitle",
      children: t('locatorRemovalTitle')
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertDetailLabel|detailLabel",
      children: t('locatorRemovalMsg')
    }), /*#__PURE__*/_jsx(AlertEditor, {
      kind: "alert",
      value: Model.getLocatorRemovedAlert(value),
      editableValues: Model.editableValuesByAlertType.locatorRemoved,
      eventData: "setLocatorRemovedAlert",
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertDetailTitle|detailTitle",
      children: t('motionDetectedTitle')
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertDetailLabel|detailLabel",
      children: t('motionDetectedMsg')
    }), /*#__PURE__*/_jsx(AlertEditor, {
      kind: "alert",
      value: Model.getMotionAfterHoursAlert(value),
      editableValues: Model.editableValuesByAlertType.motionAfterHours,
      eventData: "setMotionAfterHoursAlert",
      onChange: onModelChange
    }), /*#__PURE__*/_jsx("hr", {
      className: S.space
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertTitle|title",
      children: "Zone Alerts"
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertDetailTitle|detailTitle",
      children: t('vehicleZoneTitle')
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertDetailLabel|detailLabel",
      children: t('vehicleZoneMsg')
    }), /*#__PURE__*/_jsx(AlertEditor, {
      kind: "alert",
      value: Model.getEnteredZoneAlert(value),
      editableValues: Model.editableValuesByAlertType.enteredZone,
      eventData: "setEnteredZoneAlert",
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertDetailTitle|detailTitle",
      children: t('vehicleExitsZoneTitle')
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertDetailLabel|detailLabel",
      children: t('vehicleExistsZoneMsg')
    }), /*#__PURE__*/_jsx(AlertEditor, {
      kind: "alert",
      value: Model.getExitedZoneAlert(value),
      editableValues: Model.editableValuesByAlertType.exitedZone,
      eventData: "setExitedZoneAlert",
      onChange: onModelChange
    }), /*#__PURE__*/_jsx("hr", {
      className: S.space
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertTitleFull|title",
      children: "Locator Health Alerts"
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertDetailTitle|detailTitle",
      children: "Vehicles Paired with Offline Locators"
    }), /*#__PURE__*/_jsx(Text, {
      variant: "alertDetailLabel|detailLabel",
      children: "Enable email alerts for vehicles paired with offline locators. Email alerts will be sent out weekly to administrators and editors."
    }), /*#__PURE__*/_jsx(AlertEditor, {
      kind: "alert",
      value: Model.getOfflinePairedLocatorsAlert(value),
      editableValues: Model.editableValuesByAlertType.offlinePairedLocators,
      eventData: "setOfflinePairedLocatorsAlert",
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(SendPendingSaleMessageScreen, {
      onClose: onClose,
      onCancel: onClose,
      showDialog: showDialog,
      actions: actions,
      combinedRecipients: combinedRecipients,
      setCombinedRecipients: setCombinedRecipients
    })]
  });
}